import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { copyId, truncateIdFirstLast } from "../lib/utils";
import { useAccountList } from "@/hooks/queryHooks";
import { CopyIndicator } from "./CopyIndicator";

const RPA_BASE_URL = "https://console.cloud.google.com/storage/browser/plaza-customer-reporting-files";

const AccountList: React.FC = () => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const { data, isPending, error } = useAccountList();

  if (isPending) {
    return <div className="p-4">Loading Volo accounts...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        Error loading Volo accounts: {error.message}
      </div>
    );
  }

  const accounts = data?.accounts || [];

  return (
    <>
      {accounts.length === 0 ? (
        <p>No accounts found in Volo database.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <div className="inline-flex items-center gap-1">
                  Account Name
                </div>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <div className="inline-flex items-center gap-1">ID</div>
              </TableHead>
              <TableHead>RPA Files</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map(({ id, name, active }) => (
              <TableRow key={id}>
                <TableCell>
                  <Link
                    to={`/accounts/${encodeURIComponent(name)}`}
                    className="hover:text-blue-600 transition-colors inline-flex items-center gap-2"
                    state={{ accountId: id }}
                  >
                    {name}
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => {
                      setActiveId(id)
                      copyId(id)
                    }}
                    className="hover:text-blue-600 transition-colors cursor-pointer inline-flex items-center gap-2"
                    title="Click to copy ID"
                  >
                    {truncateIdFirstLast(id)}
                    <CopyIndicator activeId={activeId} thisId={id} />
                  </button>
                </TableCell>
                <TableCell>
                  <a className="hover:text-blue-600 transition-colors w-full cursor-pointer inline-flex justify-center items-center gap-2" href={`${RPA_BASE_URL}/${id}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default AccountList;
